/**
 * PortfolioPage Component
 *
 * Standalone page version of the Paper Portfolio (formerly PortfolioModal).
 * Accessible at /portfolio route.
 *
 * IMPORTANT DATA STRUCTURE NOTE:
 * This component uses portfolio hooks (usePortfolioSummary, useBalance, etc.) that fetch data
 * from the backend API. The backend returns responses wrapped in this structure:
 *
 *   { success: true, data: { ...actualData... }, timestamp: "..." }
 *
 * The hooks use React Query's `select` function to unwrap the nested `data` property,
 * so components receive the inner data directly:
 *
 *   summary.balance.balanceCoins  ✓ Correct (after select unwrapping)
 *   summary.data.balance.balanceCoins  ✗ Wrong (before fix - would be undefined)
 */

import React, { useState } from 'react';


import { TrendingUp, TrendingDown, Wallet, List, BarChart3, Coins, Clock3, RefreshCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';
import type { LucideIcon } from 'lucide-react';
import {
  usePortfolioSummary,
  usePortfolioTimeseries,
  usePositions,
  useClosePosition,
} from '../hooks/usePortfolio';
import { useAuth } from '../hooks/useAuth';
import { cn } from '../utils/cn';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import type { TradingPosition } from '../types/portfolio';

type ReadMoreCard = {
  icon: LucideIcon;
  title: string;
  description: string;
};

const readMoreCards: ReadMoreCard[] = [
  {
    icon: Coins,
    title: 'Virtual Coins Only',
    description: 'Paper Portfolio runs on pretend currency so you can experiment freely without risking real money.',
  },
  {
    icon: List,
    title: 'Active Position Limit',
    description: 'Keep a limited number of trades open at a time, encouraging focused, conviction-driven plays.',
  },
  {
    icon: TrendingUp,
    title: 'Long (BUY)',
    description: 'Go long to profit when the stock price increases. Perfect when you believe momentum or catalysts will push shares higher.',
  },
  {
    icon: TrendingDown,
    title: 'Short (SELL)',
    description: 'Short trades gain when the stock falls. Losses are capped with automatic closes if the stock price doubles.',
  },
];

const rewardTiers = [
  { attempt: 1, reward: 1000, note: 'Perfect read on the first try' },
  { attempt: 2, reward: 500, note: 'Strong conviction with a minor correction' },
  { attempt: 3, reward: 250, note: 'Stayed committed and got it done' },
  { attempt: 4, reward: 100, note: 'Took the scenic route, still paid' },
  { attempt: '5+', reward: 10, note: 'Participation payout—keep at it' },
];

const ReadMoreContent: React.FC = () => {
  const [showRewardBreakdown, setShowRewardBreakdown] = useState(false);

  return (
    <div className="space-y-6 text-sm text-muted-foreground">
      <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-primary/5 via-blue-500/5 to-purple-500/10 p-5 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <p className="text-base font-semibold text-foreground">A zero-risk sandbox for your stock instincts</p>
        <p className="mt-2">
          Track how your past Stock of the Day convictions would have performed. Every action happens inside a virtual environment, so you can focus on strategy, conviction, and risk management habits.
        </p>
        <p className="mt-3 text-foreground">
          Earn coins every day based on how many attempts you take, then deploy them on conviction plays.
          <button
            type="button"
            className="ml-1 font-semibold text-primary transition-colors hover:text-primary/80"
            onClick={() => setShowRewardBreakdown((prev) => !prev)}
          >
            ... see more
          </button>
        </p>
        {showRewardBreakdown && (
          <div className="mt-4 rounded-xl border border-border/70 bg-background/80 p-4 text-foreground shadow-sm dark:bg-slate-950">
            <p className="text-sm font-semibold">Daily reward tiers</p>
            <ul className="mt-3 space-y-2">
              {rewardTiers.map(({ attempt, reward, note }) => {
                const attemptLabel = typeof attempt === 'number'
                  ? `${attempt} Attempt${attempt === 1 ? '' : 's'}`
                  : `${attempt} Attempts`;
                return (
                  <li key={attemptLabel} className="flex items-start justify-between rounded-lg border border-border/60 px-3 py-2 text-sm">
                    <div>
                      <p className="font-semibold text-foreground">{attemptLabel}</p>
                      <p className="text-xs text-muted-foreground">{note}</p>
                    </div>
                    <span className="text-base font-bold text-primary">+{reward.toLocaleString()} coins</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-medium text-foreground">
          {['No real-world cash', 'Practice conviction', 'Refine risk management'].map((badge) => (
            <span key={badge} className="px-3 py-1 rounded-full border border-border/60 bg-background/80 dark:bg-slate-900/60">
              {badge}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {readMoreCards.map(({ icon: Icon, title, description }) => (
          <div key={title} className="flex gap-3 rounded-xl border border-border/70 bg-card p-4 dark:bg-slate-900/60">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground">{title}</p>
              <p>{description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-4 rounded-2xl border border-dashed border-border/70 p-4">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Clock3 className="h-4 w-4 text-primary" />
            Lifecycle safeguards
          </div>
          <ul className="mt-2 list-disc space-y-2 pl-5">
            <li>All positions automatically close after 365 days.</li>
            <li>Short positions auto-close if the stock price doubles (locks max loss at 100%).</li>
          </ul>
        </div>
        <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-muted/30 px-3 py-3 dark:bg-slate-900/40">
          <RefreshCcw className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <p className="text-sm font-semibold text-foreground">Daily pricing refresh</p>
            <p>Prices update once each day right after the Wallstreetle reset, so every move is based on the latest available data.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReadMoreModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => (
  <Modal isOpen={isOpen} onClose={onClose} title="How Paper Portfolio Works" className="max-w-2xl">
    <ReadMoreContent />
  </Modal>
);

/* ── Teaser helpers ────────────────────────────────────────────── */

const ShimmerBar = ({ className }: { className?: string }) => (
  <div className={cn('skeleton-shine h-6 w-24', className)} />
);

const LoginBanner = ({ onSignIn }: { onSignIn: () => void }) => (
  <div className="mb-4 rounded-2xl border border-primary/30 bg-primary/5 p-4 text-center dark:bg-primary/10">
    <p className="text-sm text-foreground">
      <button onClick={onSignIn} className="font-semibold text-primary hover:underline">
        Sign in
      </button>
      {' '}to start using Paper Portfolio and track your predictions
    </p>
  </div>
);

const fakePositions = [
  { id: '1', direction: 'long' as const },
  { id: '2', direction: 'short' as const },
  { id: '3', direction: 'long' as const },
];

const FakePositionCards = ({ onSignIn }: { onSignIn: () => void }) => (
  <div className="max-h-[18rem] sm:max-h-[22rem] space-y-2 sm:space-y-4 overflow-y-auto pr-1">
    {fakePositions.map((pos) => (
      <div key={pos.id} className="rounded-2xl border border-border/60 bg-background/80 p-3 sm:p-4 shadow-sm">
        {/* Mobile compact row */}
        <button
          type="button"
          onClick={onSignIn}
          className="sm:hidden w-full flex items-center justify-between gap-2 text-left"
        >
          <div className="flex items-center gap-2 min-w-0">
            <ShimmerBar className="h-5 w-14" />
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0',
                pos.direction === 'long'
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                  : 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300'
              )}
            >
              {pos.direction === 'long' ? 'LONG' : 'SHORT'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ShimmerBar className="h-4 w-24" />
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </div>
        </button>

        {/* Desktop full view */}
        <div className="hidden sm:flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShimmerBar className="h-5 w-14" />
              <span
                className={cn(
                  'rounded-full px-3 py-0.5 text-xs font-semibold',
                  pos.direction === 'long'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                    : 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300'
                )}
              >
                {pos.direction === 'long' ? 'LONG / BUY' : 'SHORT / SELL'}
              </span>
            </div>
            <ShimmerBar className="mt-2 h-4 w-28" />
            <div className="mt-3 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <div>
                <p className="text-xs uppercase tracking-wide">Coins Invested</p>
                <ShimmerBar className="mt-1 h-5 w-20" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide">Entry / Last</p>
                <ShimmerBar className="mt-1 h-5 w-28" />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Unrealized</p>
              <ShimmerBar className="mt-1 h-7 w-24" />
              <ShimmerBar className="mt-1 h-4 w-16" />
            </div>
            <Button
              size="sm"
              variant="destructive"
              className="rounded-full px-4"
              onClick={onSignIn}
            >
              Close Position
            </Button>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const TeaserChart = () => (
  <div className="relative h-[160px] sm:h-[220px] rounded-xl overflow-hidden">
    <svg
      viewBox="0 0 400 160"
      preserveAspectRatio="none"
      className="h-full w-full"
    >
      <polyline
        fill="none"
        stroke="#22c55e"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.3"
        points="0,130 30,125 60,110 90,115 120,100 150,95 180,85 210,90 240,75 270,70 300,60 330,55 360,50 400,40"
      />
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <ShimmerBar className="h-8 w-48 sm:w-56" />
    </div>
  </div>
);

export const PortfolioPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'chart' | 'positions'>('chart');
  const [chartRange, setChartRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [chartView, setChartView] = useState<'value' | 'roi'>('value');
  const [positionToClose, setPositionToClose] = useState<TradingPosition | null>(null);
  const [showReadMore, setShowReadMore] = useState(false);
  const [expandedPositionId, setExpandedPositionId] = useState<string | null>(null);

  const { user } = useAuth();
  const portfolioQueriesEnabled = !!user;
  const { data: summary, isLoading: summaryLoading } = usePortfolioSummary({ enabled: portfolioQueriesEnabled });
  const { data: timeseries, isLoading: chartLoading } = usePortfolioTimeseries(chartRange, { enabled: portfolioQueriesEnabled });
  const { data: positionsData, isLoading: positionsLoading } = usePositions('open', { enabled: portfolioQueriesEnabled });
  const positions = positionsData?.positions;
  const closePosition = useClosePosition();

  const formatCoins = (n: number) => n.toLocaleString();
  const toNumber = (value: number | string) => (typeof value === 'number' ? value : Number(value));
  const formatCurrencyFromCents = (cents?: number | null) => {
    const value = (cents ?? 0) / 100;
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  const formatPnl = (cents: number) => {
    const dollars = cents / 100;
    const sign = dollars >= 0 ? '+' : '';
    return `${sign}$${Math.abs(dollars).toFixed(2)}`;
  };

  const handleSignIn = () => {
    window.dispatchEvent(new CustomEvent('open-user-modal-for-checkout'));
  };

  // Page heading + description (shared between auth states)
  const pageHeader = (
    <>
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <h1 className="text-2xl font-bold text-foreground">Paper Portfolio</h1>
          <sup className="text-[0.65rem] font-semibold text-green-800 bg-green-100 px-2 py-0.5 rounded align-super dark:bg-green-900/30 dark:text-green-300">
            BETA
          </sup>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          This is a virtual currency portfolio that has no real-world value. The aim is to track how your thoughts on previous stocks of the day would materialize in reality.{' '}
          <button
            onClick={() => setShowReadMore(true)}
            className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
          >
            Read more
          </button>
        </p>
      </div>
    </>
  );

  const isTeaser = !user;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {pageHeader}
      {isTeaser && <LoginBanner onSignIn={handleSignIn} />}

      <div className="space-y-4 sm:space-y-6">
        {/* Summary */}
        {isTeaser ? (
          <div className="space-y-4">
            <div className="rounded-3xl border border-border/70 bg-gradient-to-br from-primary/10 via-blue-500/10 to-purple-500/10 p-3 sm:p-5 shadow-sm dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
              <div className="flex flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Available Coins</p>
                  <ShimmerBar className="mt-1 h-7 sm:h-9 w-32 sm:w-40" />
                  <p className="hidden sm:block text-sm text-muted-foreground">Ready to deploy in conviction trades</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Unrealized P&L</p>
                  <ShimmerBar className="mt-1 ml-auto h-6 sm:h-8 w-24 sm:w-32" />
                  <ShimmerBar className="mt-1 ml-auto h-4 w-20" />
                </div>
              </div>
              <div className="hidden sm:flex mt-4 flex-wrap gap-2 text-xs font-semibold text-foreground">
                {['EOD pricing refresh', 'Practice-only capital', 'Automatic risk guards'].map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-border/70 bg-background/80 px-3 py-1 text-muted-foreground"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="rounded-2xl border border-border/70 bg-card/80 p-2 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Portfolio Value</p>
                <ShimmerBar className="mt-1 h-5 sm:h-6 w-24 sm:w-28" />
                <p className="hidden sm:block text-xs text-muted-foreground">Latest close pulled after reset</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/80 p-2 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Coins at Work</p>
                <ShimmerBar className="mt-1 h-5 sm:h-6 w-20 sm:w-24" />
                <p className="hidden sm:block text-xs text-muted-foreground">Allocated across your open plays</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/80 p-2 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Open Positions</p>
                <ShimmerBar className="mt-1 h-5 sm:h-6 w-16" />
                <p className="hidden sm:block text-xs text-muted-foreground">Up to 5 trades active</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/80 p-2 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Lifetime Earned</p>
                <ShimmerBar className="mt-1 h-5 sm:h-6 w-20 sm:w-24" />
                <p className="hidden sm:block text-xs text-muted-foreground">From daily runs and bonuses</p>
              </div>
            </div>
          </div>
        ) : summaryLoading ? (
          <div className="space-y-4">
            <div className="h-32 rounded-3xl border border-border/60 bg-muted/30 animate-pulse" />
            <div className="grid gap-3 sm:grid-cols-2">
              {[0, 1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-24 rounded-2xl border border-border/60 bg-muted/20 animate-pulse"
                />
              ))}
            </div>
          </div>
        ) : summary ? (
          <div className="space-y-4">
            <div className="rounded-3xl border border-border/70 bg-gradient-to-br from-primary/10 via-blue-500/10 to-purple-500/10 p-3 sm:p-5 shadow-sm dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
              <div className="flex flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Available Coins</p>
                  <p className="mt-1 text-2xl sm:text-3xl font-semibold text-foreground">
                    ${formatCoins(summary.balance?.balanceCoins ?? 0)}
                  </p>
                  <p className="hidden sm:block text-sm text-muted-foreground">Ready to deploy in conviction trades</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Unrealized P&L</p>
                  <p
                    className={cn(
                      'mt-1 text-xl sm:text-2xl font-semibold',
                      (summary.totalUnrealizedPnlCents ?? 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'
                    )}
                  >
                    {formatPnl(summary.totalUnrealizedPnlCents ?? 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.openPositionCount ?? 0} active position{(summary.openPositionCount ?? 0) !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="hidden sm:flex mt-4 flex-wrap gap-2 text-xs font-semibold text-foreground">
                {['EOD pricing refresh', 'Practice-only capital', 'Automatic risk guards'].map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-border/70 bg-background/80 px-3 py-1 text-muted-foreground"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div className="rounded-2xl border border-border/70 bg-card/80 p-2 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Portfolio Value</p>
                <p className="mt-1 text-lg sm:text-xl font-semibold text-foreground">
                  {formatCurrencyFromCents(summary.portfolioValueCents)}
                </p>
                <p className="hidden sm:block text-xs text-muted-foreground">Latest close pulled after reset</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/80 p-2 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Coins at Work</p>
                <p className="mt-1 text-lg sm:text-xl font-semibold text-foreground">
                  ${formatCoins(summary.totalInvestedCoins ?? 0)}
                </p>
                <p className="hidden sm:block text-xs text-muted-foreground">Allocated across your open plays</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/80 p-2 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Open Positions</p>
                <p className="mt-1 text-lg sm:text-xl font-semibold text-foreground">
                  {summary.openPositionCount ?? 0} / {summary.maxPositions ?? 5}
                </p>
                <p className="hidden sm:block text-xs text-muted-foreground">Up to {summary.maxPositions ?? 5} trades active</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/80 p-2 sm:p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Lifetime Earned</p>
                <p className="mt-1 text-lg sm:text-xl font-semibold text-foreground">
                  ${formatCoins(summary.balance?.lifetimeEarned ?? 0)}
                </p>
                <p className="hidden sm:block text-xs text-muted-foreground">From daily runs and bonuses</p>
              </div>
            </div>
          </div>
        ) : null}

        <div className="rounded-3xl border border-border/70 bg-card/70 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              {activeTab === 'chart' ? (
                <BarChart3 className="h-4 w-4 text-primary" />
              ) : (
                <List className="h-4 w-4 text-primary" />
              )}
              {activeTab === 'chart' ? 'Portfolio performance' : 'Open positions'}
            </div>
            <div className="flex items-center gap-1 sm:gap-2 rounded-full border border-border/60 bg-background/70 p-1">
              <button
                type="button"
                onClick={isTeaser ? handleSignIn : () => setActiveTab('chart')}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 sm:px-4 py-1.5 text-sm font-medium transition-colors',
                  activeTab === 'chart'
                    ? 'bg-primary text-primary-foreground shadow'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                aria-pressed={activeTab === 'chart'}
              >
                <BarChart3 className="h-4 w-4" /><span className="hidden sm:inline"> Chart</span>
              </button>
              <button
                type="button"
                onClick={isTeaser ? handleSignIn : () => setActiveTab('positions')}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 sm:px-4 py-1.5 text-sm font-medium transition-colors',
                  activeTab === 'positions'
                    ? 'bg-primary text-primary-foreground shadow'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                aria-pressed={activeTab === 'positions'}
              >
                <List className="h-4 w-4" /><span className="hidden sm:inline"> Positions</span>
              </button>
            </div>
          </div>

          {activeTab === 'chart' ? (
            <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-1 sm:gap-2">
                  {(['7d', '30d', '90d'] as const).map((r) => (
                    <Button
                      key={r}
                      size="sm"
                      variant={chartRange === r ? 'primary' : 'outline'}
                      className={cn(
                        'h-7 sm:h-8 rounded-full px-2 sm:px-4 text-xs font-semibold',
                        chartRange !== r && 'border-border/70 bg-background/70 text-muted-foreground hover:text-foreground'
                      )}
                      onClick={isTeaser ? handleSignIn : () => setChartRange(r)}
                    >
                      {r.toUpperCase()}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-1 rounded-full border border-border/60 bg-background/70 p-1">
                  <Button
                    size="sm"
                    variant={chartView === 'value' ? 'primary' : 'ghost'}
                    className={cn(
                      'h-6 sm:h-8 rounded-full px-2 sm:px-3',
                      chartView !== 'value' && 'text-muted-foreground hover:text-foreground'
                    )}
                    onClick={isTeaser ? handleSignIn : () => setChartView('value')}
                  >
                    $
                  </Button>
                  <Button
                    size="sm"
                    variant={chartView === 'roi' ? 'primary' : 'ghost'}
                    className={cn(
                      'h-6 sm:h-8 rounded-full px-2 sm:px-3',
                      chartView !== 'roi' && 'text-muted-foreground hover:text-foreground'
                    )}
                    onClick={isTeaser ? handleSignIn : () => setChartView('roi')}
                  >
                    %
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-border/60 bg-background/80 p-2 sm:p-4">
                <p className="mb-2 sm:mb-3 text-center text-xs sm:text-sm font-medium text-foreground">
                  {chartView === 'value'
                    ? <><span className="sm:hidden">Invested Value</span><span className="hidden sm:inline">Invested Value (Coins in Positions + P&L)</span></>
                    : <><span className="sm:hidden">ROI</span><span className="hidden sm:inline">Return on Investment (P&L / Invested)</span></>}
                </p>
                {isTeaser ? (
                  <TeaserChart />
                ) : chartLoading ? (
                  <div className="flex justify-center py-8 sm:py-12">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="h-[160px] sm:h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={timeseries && timeseries.length > 0 ? timeseries : [{ date: '', valueCents: 0, returnPct: 0 }]}>
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(date) => {
                          if (!date) return '';
                          const d = new Date(date);
                          return `${d.getMonth() + 1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        domain={chartView === 'value' ? [0, 'auto'] : [(dataMin: number) => Math.min(dataMin, -5), (dataMax: number) => Math.max(dataMax, 5)]}
                        tickFormatter={(val) => {
                          const numericValue = toNumber(val);
                          return chartView === 'value' ? formatCurrencyFromCents(numericValue) : `${numericValue.toFixed(1)}%`;
                        }}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload || payload.length === 0) return null;
                          const data = payload[0].payload;
                          if (!data || !data.date) return null;
                          const date = new Date(data.date);
                          const formattedDate = date.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          });
                          const isPositive = data.returnPct >= 0;
                          return (
                            <div className="rounded-xl border border-border/70 bg-card/95 backdrop-blur-sm p-3 shadow-lg">
                              <p className="text-xs font-medium text-muted-foreground mb-2">{formattedDate}</p>
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between gap-4">
                                  <span className="text-xs text-muted-foreground">Invested Value</span>
                                  <span className="text-sm font-semibold text-foreground">
                                    {formatCurrencyFromCents(data.valueCents)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-4">
                                  <span className="text-xs text-muted-foreground">ROI</span>
                                  <span className={cn(
                                    'text-sm font-semibold',
                                    isPositive ? 'text-emerald-500' : 'text-rose-500'
                                  )}>
                                    {isPositive ? '+' : ''}{data.returnPct.toFixed(2)}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }}
                      />
                      {chartView === 'roi' && <ReferenceLine y={0} stroke="#888" strokeDasharray="3 3" />}
                      <Line
                        type="monotone"
                        dataKey={chartView === 'value' ? 'valueCents' : 'returnPct'}
                        stroke="#22c55e"
                        strokeWidth={2}
                        dot={false}
                      />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              <p className="text-xs font-bold text-black dark:text-white text-center">
                EOD Pricing - Refreshes after Wallstreetle reset
              </p>
            </div>
          ) : (
            <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
              {isTeaser ? (
                <FakePositionCards onSignIn={handleSignIn} />
              ) : positionsLoading ? (
                <div className="flex justify-center py-8 sm:py-12">
                  <LoadingSpinner />
                </div>
              ) : positions && positions.length > 0 ? (
                <div className="max-h-[18rem] sm:max-h-[22rem] space-y-2 sm:space-y-4 overflow-y-auto pr-1">
                  {positions.map((pos: TradingPosition) => {
                    const returnPct = pos.entryPriceCents
                      ? ((pos.unrealizedPnlCents / (pos.investedCoins * 100)) * 100).toFixed(1)
                      : '0';
                    const isPositive = pos.unrealizedPnlCents >= 0;
                    const entryDate = new Date(pos.entryDate);
                    const entryDateFormatted = entryDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    });
                    const entryPrice = formatCurrencyFromCents(pos.entryPriceCents);
                    const currentPrice = pos.currentPriceCents !== null
                      ? formatCurrencyFromCents(pos.currentPriceCents)
                      : 'Updating...';
                    const isExpanded = expandedPositionId === pos.id;

                    return (
                      <div key={pos.id} className="rounded-2xl border border-border/60 bg-background/80 p-3 sm:p-4 shadow-sm">
                        {/* Mobile compact row - tap to expand */}
                        <button
                          type="button"
                          onClick={() => setExpandedPositionId(isExpanded ? null : pos.id)}
                          className="sm:hidden w-full flex items-center justify-between gap-2 text-left"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-base font-semibold text-foreground">{pos.stockSymbol}</span>
                            <span
                              className={cn(
                                'rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0',
                                pos.direction === 'long'
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                                  : 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300'
                              )}
                            >
                              {pos.direction === 'long' ? 'LONG' : 'SHORT'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={cn('text-sm font-semibold', isPositive ? 'text-emerald-500' : 'text-rose-500')}>
                              {formatPnl(pos.unrealizedPnlCents)} ({returnPct}%)
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                            )}
                          </div>
                        </button>

                        {/* Mobile expanded details */}
                        {isExpanded && (
                          <div className="sm:hidden mt-3 pt-3 border-t border-border/60 space-y-3">
                            <p className="text-xs text-muted-foreground">Opened {entryDateFormatted}</p>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">Coins Invested</p>
                                <p className="text-sm font-semibold text-foreground">{formatCoins(pos.investedCoins)}</p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">Entry / Last</p>
                                <p className="text-sm font-semibold text-foreground">{entryPrice} → {currentPrice}</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="w-full rounded-full"
                              disabled={closePosition.isPending}
                              onClick={() => setPositionToClose(pos)}
                            >
                              Close Position
                            </Button>
                          </div>
                        )}

                        {/* Desktop full view - always visible */}
                        <div className="hidden sm:flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-semibold text-foreground">{pos.stockSymbol}</span>
                              <span
                                className={cn(
                                  'rounded-full px-3 py-0.5 text-xs font-semibold',
                                  pos.direction === 'long'
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                                    : 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300'
                                )}
                              >
                                {pos.direction === 'long' ? 'LONG / BUY' : 'SHORT / SELL'}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">Opened {entryDateFormatted}</p>
                            <div className="mt-3 flex flex-wrap gap-6 text-sm text-muted-foreground">
                              <div>
                                <p className="text-xs uppercase tracking-wide">Coins Invested</p>
                                <p className="text-base font-semibold text-foreground">
                                  {formatCoins(pos.investedCoins)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs uppercase tracking-wide">Entry / Last</p>
                                <p className="text-base font-semibold text-foreground">
                                  {entryPrice}
                                  <span className="text-xs text-muted-foreground">{' -> '}{currentPrice}</span>
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-3">
                            <div className={cn('text-right', isPositive ? 'text-emerald-500' : 'text-rose-500')}>
                              <p className="text-xs uppercase tracking-wide text-muted-foreground">Unrealized</p>
                              <p className="text-2xl font-semibold text-foreground">
                                {formatPnl(pos.unrealizedPnlCents)}
                              </p>
                              <p className="text-xs font-semibold">
                                {isPositive ? <TrendingUp className="mr-1 inline h-3 w-3" /> : <TrendingDown className="mr-1 inline h-3 w-3" />}
                                {returnPct}%
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="rounded-full px-4"
                              disabled={closePosition.isPending}
                              onClick={() => setPositionToClose(pos)}
                            >
                              Close Position
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-border/60 bg-background/60 p-4 sm:p-6 text-center">
                  <Wallet className="mx-auto h-8 sm:h-10 w-8 sm:w-10 text-muted-foreground" />
                  <p className="mt-2 sm:mt-3 font-medium text-foreground">No open positions yet</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Use your earned coins to take a long or short swing on past Stocks of the Day.
                  </p>
                </div>
              )}
              <p className="text-xs font-bold text-black dark:text-white text-center">
                EOD Pricing - Refreshes after Wallstreetle reset
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={positionToClose !== null}
        onClose={() => setPositionToClose(null)}
        onConfirm={() => {
          if (positionToClose) {
            closePosition.mutate(positionToClose.id);
            setPositionToClose(null);
          }
        }}
        title="Close Position"
        description={`Are you sure you want to close your ${positionToClose?.direction === 'long' ? 'long' : 'short'} position in ${positionToClose?.stockSymbol}?`}
        confirmText="Close Position"
        cancelText="Cancel"
        variant="danger"
        isSubmitting={closePosition.isPending}
      />
      <ReadMoreModal isOpen={showReadMore} onClose={() => setShowReadMore(false)} />
    </div>
  );
};

export default PortfolioPage;
